import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  activateAdminVariant,
  createAdminVariant,
  deactivateAdminVariant,
  fetchAdminVariants,
  updateAdminVariant,
  type CreateVariantInput,
  type UpdateVariantInput,
} from '../api/catalog-api.js';

function variantsKey(productId: string) {
  return ['admin', 'catalog', 'products', productId, 'variants'] as const;
}

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function useAdminVariants(productId: string | undefined) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: variantsKey(productId ?? ''),
    queryFn: async ({ signal }) =>
      fetchAdminVariants(productId!, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true && Boolean(productId),
    staleTime: 15_000,
  });
}

export function useAdminVariantMutations(productId: string) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: variantsKey(productId) });
    // Active-variant count / stock summary on the product list row depends
    // on variant state -- invalidate the products list too so it reflects
    // a newly created/deactivated variant.
    void queryClient.invalidateQueries({ queryKey: ['admin', 'catalog', 'products', 'list'] });
  }

  const create = useMutation({
    mutationFn: async (input: CreateVariantInput) =>
      createAdminVariant(productId, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ variantId, input }: { variantId: string; input: UpdateVariantInput }) =>
      updateAdminVariant(variantId, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const activate = useMutation({
    mutationFn: async (variantId: string) =>
      activateAdminVariant(variantId, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const deactivate = useMutation({
    mutationFn: async (variantId: string) =>
      deactivateAdminVariant(variantId, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  return { create, update, activate, deactivate };
}
